
// src/pages/FormResponses.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Container, Paper, Typography, TableHead, TableRow, TableCell, TableBody, Button, IconButton, Pagination, Stack, Box } from '@mui/material';
import {
  StyledTableContainer,
  StyledTable,
  StyledTableHeadCell,
  StyledTableRow,
} from '../theme/tableStyles';
import { useParams, useNavigate } from 'react-router-dom';
import * as endPoints from '../assets/js/endpoints';
import Loader from '../components/loader';
import { useConfirm } from '../assets/contexts/confirmContext';
import { useNotification } from '../assets/contexts/notificationContext';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ACTION_ICON_COLORS } from '../constants/iconActionColors';
import { UserContext } from '../assets/contexts/userContext';
import { Helmet } from 'react-helmet';

export default function FormResponses() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const { showNotification } = useNotification();
  const { user } = useContext(UserContext);

  const fetchForm = async () => {
    try {
      const f = await endPoints.getFormById(formId);
      setForm(f);
    } catch (_err) {
    }
  };

  const fetchResponses = async (p = page) => {
    try {
      setLoading(true);
      const resp = await endPoints.getResponsesByForm(formId, p, 5);
      setSubmissions(Array.isArray(resp.data) ? resp.data : []);
      setTotalPages(Math.ceil(resp.total / resp.per_page));
    } catch (_err) {
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async e => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm('¿Eliminar todas las respuestas?');
    if (!ok) return;
    try {
      setLoading(true);
      await endPoints.deleteResponsesByForm(formId);
      await fetchResponses(1);
      setPage(1);
      showNotification('Respuestas eliminadas', 'success');
    } catch (_err) {
      showNotification('Error eliminando respuestas', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForm();
  }, [formId]);

  useEffect(() => {
    fetchResponses(page);
  }, [formId, page]);

  const handleDeleteSubmission = async (e, submittedAt) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm('¿Eliminar este envío?');
    if (!ok) return;
    try {
      await endPoints.deleteResponsesByDate(formId, submittedAt);
      await fetchResponses(page);
      showNotification('Envío eliminado', 'success');
    } catch (_err) {
      showNotification('Error eliminando envío', 'error');
    }
  };

  if (loading) return <Loader />;

  const canManage = form && (user.hasRole('admin') || user.id === form.user_id);

  return (
    <>
      <Helmet>
        <title>Respuestas de Formulario | CES</title>
      </Helmet>
      <Container  sx={{ mt: 5, paddingBottom: 2, mb: 2 }}>
        <Box sx={{ display:'flex', justifyContent:'flex-end', mb:2 }}>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Volver</Button>
        </Box>
        <Typography variant="h4" gutterBottom>
         Respuestas de “{form.nombre}”
        </Typography>
        {submissions.length === 0 ? (
          <Typography>No hay respuestas aún.</Typography>
        ) : (
          submissions.map((sub, i) => (
            <Paper key={i} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <Typography variant="h6" gutterBottom>
                Enviado: {new Date(sub.submitted_at).toLocaleString()}
              </Typography>
              {canManage && (
                <IconButton onClick={e => handleDeleteSubmission(e, sub.submitted_at)} sx={{ color: ACTION_ICON_COLORS.delete }}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            <StyledTableContainer>
              <StyledTable size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableHeadCell>Pregunta</StyledTableHeadCell>
                  <StyledTableHeadCell>Respuesta</StyledTableHeadCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {sub.answers.map((ans, idx) => (
                  <StyledTableRow key={idx}>
                    <TableCell>{ans.question?.texto}</TableCell>
                    <TableCell>{Array.isArray(ans.respuesta) ? ans.respuesta.join(', ') : String(ans.respuesta)}</TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              </StyledTable>
            </StyledTableContainer>
            </Paper>
          ))
        )}
        {submissions.length > 0 && (
          <>
            <Stack alignItems="center" sx={{ my:2 }}>
              <Pagination count={totalPages} page={page} onChange={(e,v)=>setPage(v)} />
            </Stack>
            {canManage && (
              <Button variant="contained" sx={{ mr: 2 }} onClick={handleDeleteAll}>Borrar todas</Button>
            )}
          </>
        )}
      </Container>
    </>
  );
}
